import { Tool } from "lib/types/Tool";
import { ActivatableToolInterface } from "./ActivatableToolInterface";
import { BackgroundableToolInterface } from "./BackgroundableToolInterface";
import {
  ObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";
import { SerializableToolInterface } from "./SerializableToolInterface";

/**
 * Maintains registries for tools.
 *
 * Activatable tools are mutually exclusive: activating one automatically deactivates the previous.
 * Backgroundable tools persist independently and can all be cleared at once.
 */
export class ToolManager {
  private _activeToolId: string | null = null;
  private readonly _activatableTools = new Map<
    string,
    ActivatableToolInterface
  >();
  private readonly _backgroundableTools = new Map<
    string,
    BackgroundableToolInterface
  >();
  private readonly _serializableTools = new Map<
    string,
    SerializableToolInterface<unknown>
  >();

  private readonly _activeTool = new ObservableProperty<Tool>(Tool.none);
  private readonly _appliedBackgroundTools = new ObservableProperty<
    ReadonlySet<Tool>
  >(new Set());

  constructor(private readonly defaultTool: Tool = Tool.none) {}

  get activeTool(): ReadonlyObservableProperty<Tool> {
    return this._activeTool;
  }

  get appliedBackgroundTools(): ReadonlyObservableProperty<ReadonlySet<Tool>> {
    return this._appliedBackgroundTools;
  }

  register(
    toolId: string,
    tool:
      | ActivatableToolInterface
      | BackgroundableToolInterface
      | SerializableToolInterface<unknown>,
  ): void {
    if ("isActive" in tool) {
      this._activatableTools.set(toolId, tool);
      if (toolId === this.defaultTool) this.setActiveTool(toolId);
    }
    if ("isStateApplied" in tool) this._serializableTools.set(toolId, tool);
    if ("isApplied" in tool) {
      const backgroundable = tool as BackgroundableToolInterface;
      this._backgroundableTools.set(toolId, backgroundable);
      backgroundable.isApplied.subscribe((isApplied) => {
        const updated = new Set(this._appliedBackgroundTools.getSnapshot());
        if (isApplied) updated.add(toolId as Tool);
        else updated.delete(toolId as Tool);
        this._appliedBackgroundTools.set(updated);
      });
    }
  }

  isApplied(toolId: string): boolean {
    const tool = this._backgroundableTools.get(toolId);
    return tool ? tool.isApplied.getSnapshot() : false;
  }

  isStateApplied(toolId: string, state: unknown): boolean {
    const tool = this._serializableTools.get(toolId);
    if (!tool)
      throw new Error(
        `Tool ${toolId} is not registered as a serializable tool.`,
      );
    return tool.isStateApplied(state);
  }

  /**
   * Activate the tool with the given id, deactivating any previously active tool.
   * Pass `null` (or `Tool.none`) to deactivate the current tool and fall back to `defaultTool`.
   */
  setActiveTool(toolId: string | null): void {
    const resolvedToolId =
      toolId === null || toolId === Tool.none
        ? this.defaultTool
        : (toolId as Tool);
    const nextActiveToolId =
      resolvedToolId === Tool.none ? null : resolvedToolId;

    if (this._activeToolId === nextActiveToolId) return;
    if (this._activeToolId !== null) {
      this._activatableTools.get(this._activeToolId)?.deactivate();
    }
    this._activeToolId = nextActiveToolId;
    if (nextActiveToolId !== null) {
      this._activatableTools.get(nextActiveToolId)?.activate();
    }
    this._activeTool.set(resolvedToolId);
  }

  activateDefaultTool(): void {
    this.setActiveTool(null);
  }

  captureToolState(toolId: string): unknown {
    return this._serializableTools.get(toolId)?.capture();
  }

  restoreToolState(toolId: string, state: unknown): void {
    const tool = this._serializableTools.get(toolId);
    if (!tool)
      throw new Error(
        `Tool ${toolId} is not registered as a serializable tool.`,
      );
    tool.restore(state);
  }

  isMatchingToolState(toolId: string, state: unknown): boolean {
    const tool = this._serializableTools.get(toolId);
    if (!tool)
      throw new Error(
        `Tool ${toolId} is not registered as a serializable tool.`,
      );
    return tool.isMatchingState(state);
  }

  clearBackgroundTool(toolId: string): void {
    this._backgroundableTools.get(toolId)?.clear();
  }

  clearAllBackgroundTools(): void {
    for (const tool of this._backgroundableTools.values()) tool.clear();
  }

  /** Deactivates the active tool back to `defaultTool` and clears all background tools. */
  resetAllTools(): void {
    this.activateDefaultTool();
    this.clearAllBackgroundTools();
  }
}
